import { z } from 'zod';
import { endpoint } from './endpoint.js';
import {
  UserAdminSchema,
  UserAdminListItemSchema,
  CreateUserSchema,
  UpdateUserSchema,
  UserPermissionOverrideSchema,
  UpdateUserPermissionOverridesSchema,
  AdminSetPasswordSchema,
} from '../schemas/user-admin.js';
import { createPaginatedResponseSchema } from '../schemas/pagination.js';

const UserAdminListResponseSchema = z.object({
  items: z.array(UserAdminListItemSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

const ListUsersQuerySchema = z.object({
  roleCode: z.string().optional(),
  isActive: z.boolean().optional(),
  search: z.string().optional(),
  page: z.number().optional(),
  pageSize: z.number().max(200).optional(),
});

export const adminUsers = {
  /** GET /admin/users — paginated user list (admin only) */
  listUsers: endpoint('GET', '/admin/users', ListUsersQuerySchema, UserAdminListResponseSchema),

  /** GET /admin/users/:id — user detail (admin only) */
  getUser: endpoint('GET', '/admin/users/:id', z.object({ id: z.number() }), UserAdminSchema),

  /** POST /admin/users — create user (admin only) */
  createUser: endpoint('POST', '/admin/users', CreateUserSchema, UserAdminSchema),

  /** PATCH /admin/users/:id — update user (admin only) */
  updateUser: endpoint(
    'PATCH',
    '/admin/users/:id',
    z.object({ id: z.number() }).merge(UpdateUserSchema),
    UserAdminSchema,
  ),

  /** GET /admin/users/:id/permission-overrides — list overrides (admin only) */
  listUserOverrides: endpoint(
    'GET',
    '/admin/users/:id/permission-overrides',
    z.object({ id: z.number() }),
    z.array(UserPermissionOverrideSchema),
  ),

  /** PUT /admin/users/:id/permission-overrides — replace overrides (admin only) */
  updateUserOverrides: endpoint(
    'PUT',
    '/admin/users/:id/permission-overrides',
    z.object({ id: z.number() }).merge(UpdateUserPermissionOverridesSchema),
    z.array(UserPermissionOverrideSchema),
  ),

  // PATCH /admin/users/:id/password — 204 No Content. Response schema MUST be
  // z.void() (see packages/contracts/AGENTS.md) so callEndpoint's 204 branch
  // parses `undefined` instead of trying to JSON.parse an empty body.
  /** PATCH /admin/users/:id/password — admin sets/changes a user's password (admin only) */
  setPassword: endpoint(
    'PATCH',
    '/admin/users/:id/password',
    z.object({ id: z.number() }).merge(AdminSetPasswordSchema),
    z.void(),
  ),
};

// Dummy import to avoid unused import warning
void createPaginatedResponseSchema;
